import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { LogIn, Trash2, MoreHorizontal, UserCog, FileX, ChevronLeft, ChevronRight } from "lucide-react";
import { Admin } from "@/types/company";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { useState, useEffect } from 'react';

interface UserTableProps {
  users: Admin[];
  onDirectLogin: (userId: string) => void;
  onDelete: (userId: string) => void;
  onSwitchToManager: (userId: string) => void;
  onEdit: (user: Admin) => void;
}

export function UserTable({ 
  users, 
  onDirectLogin, 
  onDelete,
  onSwitchToManager,
  onEdit
}: UserTableProps) {
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    user: Admin | null;
  }>({
    isOpen: false,
    user: null,
  });

  // Sayfalama için state'ler
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(users.length / itemsPerPage);

  // Mevcut sayfada gösterilecek kullanıcıları belirle
  const currentUsers = users.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Sayfa değiştirme fonksiyonu
  const changePage = (page: number) => {
    setCurrentPage(page);
  };

  // Önceki ve sonraki sayfalara geçiş fonksiyonları
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Kullanıcı listesi değiştiğinde 1. sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [users.length]);

  return (
    <div className="space-y-4">
      {/* Kullanıcı Tablosu */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[250px]">İsim</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[250px]">Email</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[150px]">Rol</TableHead>
              <TableHead className="py-4 text-slate-700 font-bold text-sm w-[250px]">Firma</TableHead>
              <TableHead className="text-right py-4 pr-6 text-slate-700 font-bold text-sm w-[100px]">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center text-slate-500">
                    <FileX className="h-10 w-10 mb-2 text-slate-400" />
                    <p>Kullanıcı bulunamadı.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              currentUsers.map((user) => (
                <TableRow key={user._id} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors">
                  <TableCell className="py-4 font-semibold text-slate-900">{user.name}</TableCell>
                  <TableCell className="py-4 text-slate-700">{user.email}</TableCell>
                  <TableCell className="py-4">
                    {user.role === 'super_admin' ? (
                      <div className="inline-flex items-center rounded-lg bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-800">
                        <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-purple-600"></div>
                        Süper Admin
                      </div>
                    ) : (
                      <div className="inline-flex items-center rounded-lg bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                        <div className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-600"></div>
                        Yönetici
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-4 text-slate-700 font-medium">
                    {user.role === 'manager' && user.company ? (
                      user.company.name
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right py-4 pr-6">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 rounded-lg hover:bg-slate-100"
                        >
                          <span className="sr-only">Menüyü aç</span>
                          <MoreHorizontal className="h-5 w-5 text-slate-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onEdit(user)}
                          className="cursor-pointer"
                        >
                          <UserCog className="mr-2 h-4 w-4" />
                          <span>Düzenle</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {user.role === 'manager' && (
                          <>
                            <DropdownMenuItem
                              onClick={() => onSwitchToManager(user._id)}
                              className="cursor-pointer"
                            >
                              <UserCog className="mr-2 h-4 w-4" />
                              <span>Yönetici Olarak Devam Et</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onDirectLogin(user._id)}
                              className="cursor-pointer"
                            >
                              <LogIn className="mr-2 h-4 w-4" />
                              <span>Yeni Sekmede Aç</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            setDeleteDialog({
                              isOpen: true,
                              user,
                            });
                          }}
                          className="cursor-pointer text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Sil</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Sayfalama */}
      {users.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{users.length}</span> kullanıcı içinden{" "}
            <span className="font-semibold text-slate-800">{(currentPage - 1) * itemsPerPage + 1}</span>-
            <span className="font-semibold text-slate-800">{Math.min(currentPage * itemsPerPage, users.length)}</span>{" "}
            arası gösteriliyor
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Önceki sayfa"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Önceki</span>
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let startPage = Math.max(1, currentPage - 2);
                if (currentPage > totalPages - 2) {
                  startPage = Math.max(1, totalPages - 4);
                }

                const page = startPage + i;
                if (page <= totalPages) {
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => changePage(page)}
                      className={`h-10 min-w-10 px-3 rounded-lg ${
                        currentPage === page
                          ? "bg-blue-600 hover:bg-blue-700 text-white pointer-events-none"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {page}
                    </Button>
                  );
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              aria-label="Sonraki sayfa"
            >
              <span className="hidden sm:inline">Sonraki</span>
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Silme Dialog */}
      {deleteDialog.isOpen && deleteDialog.user && (
        <DeleteUserDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, user: null })}
          onConfirm={() => {
            if (deleteDialog.user) {
              onDelete(deleteDialog.user._id);
            }
          }}
          userName={deleteDialog.user.name}
          userRole={deleteDialog.user.role}
        />
      )}
    </div>
  );
}
